import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, config } from 'rxjs';
import { ConfigService } from '../../../../shared/services/config.service';
import { UserService } from '../../../../shared/services/user.service';
import { DocumentCard } from '../models/document-card.model';
import { DocumentCategory } from '../models/document-category.model';
import { DocumentAttributeValue } from '../models/document-attribute-value.model';
import { DocumentAttribute } from '../models/document-attribute.model';
import { DocumentCode } from '../models/document-code.model';
import { DocumentTag } from '../models/document-tag.model';
import { DocumentVersion } from "../models/document-version.model";
import { DocumentCodeList } from '../models/document-code-list';
import {DocumentFile} from "../models/document-file.model";
import { DepartmentAffiliationVm, DocumentAffiliationTypeEnum, DocumentAffiliationVm, EquipmentAffiliationVm, ResourceAffiliationVm, WorkgroupAffiliationVm } from '../models/document-affiliation.model';
import { Department } from '../../../admin/_api/models/department.model';

@Injectable({
  providedIn: 'root'
})
export class DocumentCardService {

  constructor(private http: HttpClient, private configService: ConfigService, private userService: UserService) { }

  private get httpOptions() {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'APIKEY': 'ams-direct-api-document-key',
        'SessionId': this.userService.sessionId
      })
    };
  }

  get(id: number): Observable<DocumentCard> {
    return this.http.get<DocumentCard>(this.configService.config.documentUrl + `/api/DocumentCard/Get/` + id, this.httpOptions);
  }

  getAll(): Observable<DocumentCard[]> {
    return this.http.get<DocumentCard[]>(this.configService.config.documentUrl + `/api/DocumentCard/Get`, this.httpOptions);
  }

  getDocumentCategories(): Observable<DocumentCategory[]> {
    return this.http.get<DocumentCategory[]>(this.configService.config.documentUrl + `/api/DocumentCard/GetDocumentCategories`, this.httpOptions);
  }

  getDocumentTags(): Observable<DocumentTag[]> {
    return this.http.get<DocumentTag[]>(this.configService.config.documentUrl + `/api/DocumentCard/GetDocumentTags`, this.httpOptions);
  }

  getDocumentCodes(documentId: number): Observable<DocumentCode[]> {
    return this.http.get<DocumentCode[]>(this.configService.config.documentUrl + `/api/DocumentCard/GetDocumentCodes/` + documentId, this.httpOptions);
  }

  GetDocumentCodesWithListsOnDocumentId(documentId: number): Observable<DocumentCodeList[]> {
    return this.http.get<DocumentCodeList[]>(this.configService.config.documentUrl + `/api/Documentcard/GetDocumentCodesWithListsOnDocumentId/` + documentId, this.httpOptions);
  }

  UpdateDocumentCodeLists(documentId: number, documentCodeLists: DocumentCodeList[]): Observable<any> {
    let data = {
      documentId: documentId, documentCodeLists: documentCodeLists,
    };
    return this.http.post<any>(this.configService.config.documentUrl + `/api/DocumentCard/UpdateDocumentCodeListForDocument`, JSON.stringify(data), this.httpOptions);
  }

  getDocumentAttributes(documentId: number): Observable<DocumentAttribute[]> {
    return this.http.get<DocumentAttribute[]>(this.configService.config.documentUrl + `/api/DocumentCard/GetDocumentAttributes/` + documentId, this.httpOptions);
  }

  GetDocumentAttributesWithValuesOnDocumentId(documentId: number): Observable<DocumentAttributeValue[]> {
    return this.http.get<DocumentAttributeValue[]>(this.configService.config.documentUrl + `/api/Documentcard/GetDocumentAttributesWithValuesOnDocumentId/` + documentId, this.httpOptions);
  }

  updateDocumentAttributeValue(docAttribValue: any): Observable<any> {
    return this.http.post<any>(this.configService.config.documentUrl + '/api/Documentcard/UpdateDocumentAttributeValue', docAttribValue, this.httpOptions);
  }

  saveDetails(document: DocumentCard): Observable<any> {
    return this.http.post<any>(this.configService.config.documentUrl + '/api/DocumentCard/UpdateDocumentDetails', document, this.httpOptions);
  }
  
  updateDocumentVersion(documentVersion: DocumentVersion): Observable<any> {
    return this.http.post<any>(this.configService.config.documentUrl + '/api/DocumentCard/UpdateDocumentVersion', documentVersion, this.httpOptions);
  }

  addDocument(document: DocumentCard): Observable<any> {
    return this.http.post(this.configService.config.documentUrl + '/api/DocumentCard/AddDocument', document, this.httpOptions);
  }

  getDownloadFileURL(documentFileId: number, languageId: number = 1): string {
    return this.configService.config.documentUrl + `/api/DocumentCard/DownloadFile/${documentFileId}?languageId=${languageId}&SessionId=${this.userService.sessionId}`;
  }

  setFavorite(documentId: number, value: boolean): Observable<any> {
    return this.http.post(this.configService.config.documentUrl + `/api/DocumentCard/SetFavorite?documentId=${documentId}&value=${value}`, "", this.httpOptions);
  }

  isFavorite(DocumentId: number): Observable<any> {
    return this.http.get(this.configService.config.documentUrl + `/api/DocumentCard/IsFavorite?documentId=${DocumentId}`, this.httpOptions);
  }

  GetDocumentFileFormatId(fileType: string): Observable<number> {
    return this.http.get<number>(this.configService.config.documentUrl + `/api/DocumentCard/GetDocumentFileFormatId?filetype=${fileType}`, this.httpOptions);
  }

  SetViewed(documentId: number) {
     return this.http.post(this.configService.config.documentUrl + `/api/DocumentCard/SetViewed?documentId=${documentId}`, "", this.httpOptions);
  }

  GetDepartments(): Observable<Department[]> {
    return this.http.get<Department[]>(this.configService.config.documentUrl + `/api/DocumentCard/GetDepartments`, this.httpOptions);
  }

  GetEquipments(): Observable<string[]> {
    return this.http.get<string[]>(this.configService.config.documentUrl + `/api/DocumentCard/GetEquipments`, this.httpOptions);
  }

  GetWorkstationResources(): Observable<string[]> {
    return this.http.get<string[]>(this.configService.config.documentUrl + `/api/DocumentCard/GetWorkstationResources`, this.httpOptions);
  }

  GetEquipmentAndWorkstationResourcesByDepartmentIds(departmentIds: number[]): Observable<any> {
    return this.http.post(this.configService.config.documentUrl + `/api/DocumentCard/GetEquipmentAndWorkstationResourcesByDepartmentIds`, departmentIds, this.httpOptions);
  }

  getDocumentFiles(documentId: number): Observable<DocumentFile[]> {
    return this.http.get<DocumentFile[]>(this.configService.config.documentUrl + `/api/DocumentCard/GetDocumentFiles?documentId=${documentId}`, this.httpOptions);
  }

  getCurrentDocumentVersion(documentId: number): Observable<DocumentVersion> {
    return this.http.get<DocumentVersion>(this.configService.config.documentUrl + `/api/DocumentCard/GetCurrentVersion?documentId=${documentId}`, this.httpOptions);
  }

  deleteDocumentCard(documentId: number): Observable<DocumentFile[]> {
    return this.http.get<DocumentFile[]>(this.configService.config.documentUrl + `/api/DocumentCard/DeleteDocumentCard?documentId=${documentId}`, this.httpOptions);
  }

  GetDocumentAffiliations(documentId: number): Observable<DocumentAffiliationVm> {
    return this.http.get<DocumentAffiliationVm>(this.configService.config.documentUrl + `/api/DocumentCard/GetAffiliations?documentId=${documentId}`, this.httpOptions);
  }

  UpdateAffiliation(DocumentId: number, Assign: boolean, AffiliationTypeId: DocumentAffiliationTypeEnum, AffiliationIds: number[]): Observable<DocumentAffiliationVm> {
    return this.http.post<DocumentAffiliationVm>(this.configService.config.documentUrl + `/api/DocumentCard/UpdateAffiliation`, { DocumentId, Assign, AffiliationTypeId, AffiliationIds }, this.httpOptions);
  }

  GetDepartmentAffiliation(documentId: number): Observable<DepartmentAffiliationVm> {
    return this.http.get<DepartmentAffiliationVm>(this.configService.config.documentUrl + `/api/DocumentCard/GetDepartmentAffiliationVm?documentId=${documentId}`, this.httpOptions);
  }

  GetWorkgroupAffiliation(documentId: number, departmentId: number): Observable<WorkgroupAffiliationVm> {
    return this.http.get<WorkgroupAffiliationVm>(this.configService.config.documentUrl + `/api/DocumentCard/GetWorkgroupAffiliationVm?documentId=${documentId}&departmentId=${(departmentId != null ? departmentId.toString() : "")}`, this.httpOptions);
  }

  GetResourceAffiliation(documentId: number, departmentId: number): Observable<ResourceAffiliationVm> {
    return this.http.get<ResourceAffiliationVm>(this.configService.config.documentUrl + `/api/DocumentCard/GetResourceAffiliationVm?documentId=${documentId}&departmentId=${(departmentId != null ? departmentId.toString() : "")}`, this.httpOptions);
  }

  GetEquipmentAffiliation(documentId: number, departmentId: number): Observable<EquipmentAffiliationVm> {
    return this.http.get<EquipmentAffiliationVm>(this.configService.config.documentUrl + `/api/DocumentCard/GetEquipmentAffiliationVm?documentId=${documentId}&departmentId=${(departmentId != null ? departmentId.toString() : "")}`, this.httpOptions);
  }

  CreateNewVersion(documentCardDocumentId: number) {
    return this.http.get<void>(this.configService.config.documentUrl + `/api/DocumentCard/CreateNewDocumentVersion?documentCardDocumentId=${documentCardDocumentId}`, this.httpOptions);
  }

  DeleteDocumentFileLanguage(documentFileId: number, languageId: number) {
    return this.http.get<void>(this.configService.config.documentUrl + `/api/DocumentCard/DeleteDocumentFileLanguage?documentFileId=${documentFileId}&languageId=${languageId}`, this.httpOptions);
  }
}
